import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Bot, Eye, Brain, Zap, BookOpen, ArrowRight, Shield,
  DollarSign, Tag, Package, Truck, SmilePlus, Search, Share2,
  CheckCircle, AlertTriangle, Copy, ChevronRight, Settings, Lock
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsAgentsOverview() {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const lifecycleSteps = [
    {
      icon: Eye,
      title: 'Observe',
      desc: 'Agents continuously monitor incoming signals — price changes, order events, inventory updates, customer reviews, search rankings, and more. They build a real-time picture of your commerce operations.',
      color: '#38BDF8',
    },
    {
      icon: Brain,
      title: 'Decide',
      desc: 'Using the Decision Graph, agents evaluate signals against your guardrails, historical patterns, and business context. They calculate a confidence score for each potential action.',
      color: '#7A5CFF',
    },
    {
      icon: Zap,
      title: 'Act',
      desc: 'Based on their trust level, agents either take action autonomously (adjust price, create deal, reorder stock) or submit a recommendation for your approval.',
      color: '#3EF1C7',
    },
    {
      icon: BookOpen,
      title: 'Learn',
      desc: 'Every action outcome is tracked. Agents learn from your approvals, rejections, and overrides to improve future decisions. The more you interact, the smarter they get.',
      color: '#FFD166',
    },
  ];

  const agents = [
    {
      icon: DollarSign,
      name: 'Pricing Agent',
      tagline: 'Competitive pricing, automated',
      desc: 'Monitors competitor prices across marketplaces, analyzes price elasticity, and recommends or executes optimal pricing strategies. Supports MAP compliance, dynamic pricing rules, and margin protection guardrails.',
      observes: ['Competitor price changes', 'Sales velocity shifts', 'Margin threshold alerts', 'Seasonal demand patterns'],
      decides: ['Optimal price points per SKU', 'When to match vs. undercut competitors', 'Dynamic pricing windows'],
      actions: ['Adjust product prices', 'Send price change notifications', 'Generate pricing reports'],
      guardrails: ['Minimum margin threshold (default 20%)', 'Maximum daily price change (10%)', 'MAP compliance enforcement', 'Blackout date restrictions'],
      color: '#3EF1C7',
    },
    {
      icon: Tag,
      name: 'Deal Agent',
      tagline: 'Promotions that perform',
      desc: 'Creates, manages, and optimizes promotional campaigns. Analyzes deal performance in real-time, suggests discount depths based on inventory age and margin, and auto-expires underperforming deals.',
      observes: ['Inventory age thresholds', 'Conversion rate drops', 'Competitor promotions', 'Seasonal opportunities'],
      decides: ['Which products to promote', 'Discount depth and duration', 'When to extend or expire deals'],
      actions: ['Create deals on thedeals.ai', 'Publish to distribution channels', 'Auto-expire underperformers'],
      guardrails: ['Maximum discount percentage (default 40%)', 'Minimum units in stock before promoting', 'Maximum concurrent deals per category', 'Budget caps per campaign'],
      color: '#7A5CFF',
    },
    {
      icon: Package,
      name: 'Inventory Agent',
      tagline: 'Never stock out again',
      desc: 'Predicts stockouts using sales velocity and lead time analysis. Calculates optimal reorder points, suggests safety stock levels, and alerts you before problems happen — not after.',
      observes: ['Stock level changes', 'Sales velocity trends', 'Supplier lead times', 'Seasonal demand forecasts'],
      decides: ['Reorder timing and quantity', 'Safety stock levels', 'Which supplier to use'],
      actions: ['Generate purchase orders', 'Send low-stock alerts', 'Update reorder points'],
      guardrails: ['Maximum order value per PO', 'Minimum days-of-supply threshold', 'Approved supplier list only', 'Budget limits per reorder cycle'],
      color: '#FFD166',
    },
    {
      icon: Truck,
      name: 'Fulfillment Agent',
      tagline: 'Ship it right, every time',
      desc: 'Tracks shipments across carriers, detects delivery exceptions in real-time, proactively notifies customers of delays, and monitors fulfillment SLAs. Flags patterns like repeated carrier issues.',
      observes: ['Shipping status updates', 'Delivery exception alerts', 'Carrier performance data', 'SLA breach warnings'],
      decides: ['When to escalate delays', 'Carrier selection for new shipments', 'Customer notification timing'],
      actions: ['Send proactive delay notifications', 'Flag carrier issues', 'Generate fulfillment reports'],
      guardrails: ['SLA breach threshold (default 24h)', 'Maximum auto-refund amount', 'Escalation rules for repeat issues', 'Carrier performance minimums'],
      color: '#FF6B8A',
    },
    {
      icon: SmilePlus,
      name: 'CX Agent',
      tagline: 'Customer sentiment, quantified',
      desc: 'Monitors customer sentiment across reviews, support tickets, and social mentions. Calculates NPS trends, identifies emerging issues before they escalate, and routes critical feedback for immediate attention.',
      observes: ['New reviews and ratings', 'Support ticket sentiment', 'Social media mentions', 'NPS score changes'],
      decides: ['Escalation priority level', 'Response templates to suggest', 'Trend alerts to surface'],
      actions: ['Route critical tickets', 'Generate sentiment reports', 'Trigger escalation workflows'],
      guardrails: ['Never auto-respond to customers', 'Escalate all 1-star reviews', 'Minimum review volume before trends', 'PII handling restrictions'],
      color: '#38BDF8',
    },
    {
      icon: Search,
      name: 'SEO Agent',
      tagline: 'Be found, be bought',
      desc: 'Optimizes product titles, descriptions, and metadata for search engine visibility. Monitors keyword rankings, suggests content improvements, and tracks organic traffic impact per product.',
      observes: ['Keyword ranking changes', 'Search impression data', 'Click-through rate shifts', 'Competitor content updates'],
      decides: ['Title and description rewrites', 'Meta tag optimization', 'Keyword targeting strategy'],
      actions: ['Update product titles/descriptions', 'Generate SEO reports', 'Suggest image alt text'],
      guardrails: ['Brand voice guidelines', 'Maximum title length (70 chars)', 'No keyword stuffing (density < 3%)', 'Require approval for title changes'],
      color: '#FB923C',
    },
    {
      icon: Share2,
      name: 'Distribution Agent',
      tagline: 'Everywhere your customers are',
      desc: 'Manages product distribution across multiple channels and marketplaces. Syncs listings, pricing, and inventory across Amazon, eBay, Walmart, and other platforms. Detects channel conflicts.',
      observes: ['Channel performance metrics', 'Listing sync failures', 'Cross-channel price conflicts', 'New marketplace opportunities'],
      decides: ['Channel prioritization', 'Price parity enforcement', 'Listing optimization per channel'],
      actions: ['Sync listings across channels', 'Resolve price conflicts', 'Generate channel reports'],
      guardrails: ['Price parity rules across channels', 'Approved channel list only', 'Maximum listing changes per day', 'Channel-specific compliance rules'],
      color: '#A78BFA',
    },
  ];

  const decisionFlowCode = `// Simplified agent decision flow

signal_received = await agent.observe(event)
  // e.g., competitor lowered price by 15%

guardrails_check = await agent.checkGuardrails({
  min_margin: 0.20,         // Never go below 20% margin
  max_change_pct: 0.10,     // Max 10% price change per day
  map_compliance: true,      // Respect MAP pricing
  blackout_dates: ["2024-11-29"]  // No changes on Black Friday
})

if (!guardrails_check.passed) {
  return agent.log("Blocked by guardrail: " + guardrails_check.reason)
}

confidence = await agent.scoreConfidence({
  signal_strength: 0.85,    // How strong is the signal?
  historical_success: 0.72, // Past success rate for similar actions
  data_quality: 0.90        // How complete is our data?
})
// → confidence = 0.82

if (confidence >= agent.trust_level.auto_threshold) {
  // Trust level allows autonomous action
  await agent.execute(action)
  await agent.notify("Price adjusted: $49.99 → $44.99")
} else {
  // Needs human approval
  await agent.requestApproval({
    action: "Reduce price to $44.99",
    reason: "Competitor dropped to $42.99, maintaining margin at 22%",
    confidence: 0.82,
    expires_in: "24h"
  })
}`;

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          AI Agents
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          The Agent Org Chart
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Commerce Index deploys one AI CEO (goal_manager), 13 department heads (VP Commerce, Head Supply Chain, Head Pricing, Head E-commerce, Head Retail, Head Wholesale, Head Finance, Head Customer Service, Head Tax, Head Brand & IP, Head Corp Compliance, CMO, Head Creator Success), and 60+ specialist worker agents. Each agent
          observes signals, makes decisions within guardrails, and learns from outcomes. Together, they
          form an autonomous commerce team.
        </p>
      </div>

      {/* Agent Lifecycle */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          The Agent Lifecycle
        </h2>
        <p className="text-[#94A3B8] mb-6">
          Every agent follows the same four-phase lifecycle. This creates a consistent, predictable, and
          auditable pattern across all agent operations:
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {lifecycleSteps.map((step, i) => {
            const Icon = step.icon;
            return (
              <Card key={i} className="bg-[#0D1628] border-[#22304A]">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${step.color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: step.color }} />
                    </div>
                    <div>
                      <span className="text-[#64748B] text-xs font-mono uppercase">Phase {i + 1}</span>
                      <h3 className="text-white font-semibold">{step.title}</h3>
                    </div>
                  </div>
                  <p className="text-sm text-[#94A3B8]">{step.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <div className="mt-4 flex items-center justify-center gap-2 text-[#64748B] text-sm">
          <span>Observe</span>
          <ArrowRight className="w-4 h-4" />
          <span>Decide</span>
          <ArrowRight className="w-4 h-4" />
          <span>Act</span>
          <ArrowRight className="w-4 h-4" />
          <span>Learn</span>
          <ArrowRight className="w-4 h-4" />
          <span className="text-[#3EF1C7]">Repeat</span>
        </div>
      </section>

      {/* The 7 Agents */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Meet the Agents
        </h2>
        <div className="space-y-4">
          {agents.map((agent, i) => {
            const Icon = agent.icon;
            return (
              <Card key={i} className="bg-[#0D1628] border-[#22304A]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${agent.color}20` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: agent.color }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-semibold text-white">{agent.name}</h3>
                        <span className="text-sm" style={{ color: agent.color }}>{agent.tagline}</span>
                      </div>
                      <p className="text-sm text-[#94A3B8] mb-3">{agent.desc}</p>
                      <div className="grid md:grid-cols-2 gap-3 mt-1">
                        <div>
                          <span className="text-xs text-[#64748B] uppercase font-mono mb-1.5 block">Observes</span>
                          <ul className="space-y-1">
                            {agent.observes.map((item, j) => (
                              <li key={j} className="text-xs text-[#94A3B8] flex items-start gap-1.5">
                                <Eye className="w-3 h-3 text-[#38BDF8] flex-shrink-0 mt-0.5" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <span className="text-xs text-[#64748B] uppercase font-mono mb-1.5 block">Decides</span>
                          <ul className="space-y-1">
                            {agent.decides.map((item, j) => (
                              <li key={j} className="text-xs text-[#94A3B8] flex items-start gap-1.5">
                                <Brain className="w-3 h-3 text-[#7A5CFF] flex-shrink-0 mt-0.5" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <span className="text-xs text-[#64748B] uppercase font-mono mb-1.5 block">Actions</span>
                          <ul className="space-y-1">
                            {agent.actions.map((item, j) => (
                              <li key={j} className="text-xs text-[#94A3B8] flex items-start gap-1.5">
                                <Zap className="w-3 h-3 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <span className="text-xs text-[#64748B] uppercase font-mono mb-1.5 block">Default Guardrails</span>
                          <ul className="space-y-1">
                            {agent.guardrails.map((item, j) => (
                              <li key={j} className="text-xs text-[#94A3B8] flex items-start gap-1.5">
                                <Shield className="w-3 h-3 text-[#FFD166] flex-shrink-0 mt-0.5" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* How Agents Make Decisions */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          How Agents Make Decisions
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Every agent decision follows a structured flow: signal received, guardrails checked, confidence scored,
          and action taken or approval requested. Here is a simplified example:
        </p>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(decisionFlowCode)}
            className="absolute top-3 right-3 p-2 rounded-md bg-[#1E293B] hover:bg-[#334155] transition-colors z-10"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4 text-[#64748B]" />
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{decisionFlowCode}</code>
          </pre>
        </div>
        <div className="mt-6 grid md:grid-cols-4 gap-3">
          {[
            { step: '1', label: 'Signal Received', desc: 'An event triggers the agent', color: '#38BDF8' },
            { step: '2', label: 'Guardrails Checked', desc: 'Business rules validated', color: '#7A5CFF' },
            { step: '3', label: 'Confidence Scored', desc: 'Action certainty calculated', color: '#FFD166' },
            { step: '4', label: 'Action or Approval', desc: 'Execute or request review', color: '#3EF1C7' },
          ].map((item, i) => (
            <div key={i} className="text-center p-3 bg-[#1E293B] rounded-lg border border-[#334155]">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2"
                style={{ backgroundColor: `${item.color}20` }}
              >
                <span className="text-sm font-bold" style={{ color: item.color }}>{item.step}</span>
              </div>
              <h4 className="text-white text-sm font-medium mb-1">{item.label}</h4>
              <p className="text-xs text-[#64748B]">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Related Documentation */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Related Documentation
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              icon: Shield,
              title: 'Trust Ladder',
              desc: 'Configure agent autonomy levels — from observe-only to fully autonomous.',
              link: '/docs/trust-ladder',
            },
            {
              icon: AlertTriangle,
              title: 'Guardrails',
              desc: 'Set business rules that agents must respect: margins, limits, blackouts, and more.',
              link: '/docs/guardrails',
            },
            {
              icon: Settings,
              title: 'Agent Admin',
              desc: 'Enable, disable, configure, and monitor individual agents from the dashboard.',
              link: '/docs/agent-admin',
            },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <Link key={i} to={item.link}>
                <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer h-full">
                  <CardContent className="p-5">
                    <Icon className="w-6 h-6 text-[#3EF1C7] mb-3" />
                    <h3 className="text-white font-medium mb-2">{item.title}</h3>
                    <p className="text-sm text-[#94A3B8]">{item.desc}</p>
                    <span className="text-[#3EF1C7] text-sm mt-3 inline-flex items-center gap-1">
                      Learn more <ArrowRight className="w-3 h-3" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      {/* What's Next */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          What's Next?
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <ul className="space-y-3">
              {[
                { text: 'Trust Ladder Configuration', link: '/docs/trust-ladder' },
                { text: 'Guardrails Reference', link: '/docs/guardrails' },
                { text: 'Set up OpenClaw messaging', link: '/docs/openclaw' },
                { text: 'Onboarding Checklist', link: '/docs/onboarding-checklist' },
                { text: 'Commerce Health Score', link: '/docs/ci-score' },
              ].map((item, i) => (
                <li key={i}>
                  <Link to={item.link} className="flex items-center gap-2 text-[#3EF1C7] hover:underline">
                    <ChevronRight className="w-4 h-4" />
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
